import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { MainForm, RequiredNotification } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { usePostSalaryFormMutation } from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/api/salaryCertificateApiSlice';
import { salaryCertificateActions } from '@Features/EmployeeSelfService/requests/SalaryCertificate/store/salaryRequestSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { getSalaryTypeDictSelector } from '@Store/selectors/dictionarySelector';
import { NotificationContext } from '@Context/NotificationContext';
import { RequestError } from '@Types/api/form';
import { transformDictToOptions } from '@Utils/transformToOptions';
import { FooterActions } from '@Enum/index';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { SalaryStatusCodes } from '@Enum/api/SalaryStatusCodes.enum';
import { validateForm } from '@Features/utils/validateForm';

import { SalaryCertificateFormSchema } from '../validationSchema';
import { SalaryCertificateFormValues } from '../types';
import RequestDetailsSection from '../components/RequestDetailsSection';
import { initialValuesForm } from '../initialValuesForm';
import { descriptionAlert } from '../descriptionAlert';

export const SalaryCertificateEditForm: React.FC = () => {
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const dispatch = useAppDispatch();
  const { requestId: idRequest } = useParams();
  const { handleBackToListRequests } = useNavigationToListRequests();

  const { data: editDataForm } = useGetHrmRequestFormQuery(idRequest!, {
    refetchOnMountOrArgChange: true,
  });
  const requisitionInfo = useRequisitionInfoExistingReq(editDataForm, 'Salary');

  const salaryTypes = useAppSelector(getSalaryTypeDictSelector);
  const currentUser = useAppSelector(currentUserSelector);

  const [cancelRequest] = useCancelHrmRequestMutation();
  const [postSalaryForm] = usePostSalaryFormMutation();
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [submitForm] = useSubmitHrmRequestMutation();

  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);

  useEffect(
    () => () => {
      dispatch(salaryCertificateActions.setSalaryCertificate(null));
    },
    [idRequest]
  );

  const handleSubmit = useCallback(
    (values: SalaryCertificateFormValues) => {
      const submitSalaryForm = async (): Promise<void> => {
        const body = {
          requestId: editDataForm?.requestId,
          empNumber: editDataForm?.empNumber as string,
          leaveTypeID: Number(values.purpose),
          startDate: values.requiredByDate,
          destination: values.language,
          remarks: values.remarks,
        };

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await postSalaryForm(body).unwrap();

          dispatch(
            salaryCertificateActions.setSalaryCertificate({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitForm(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitSalaryForm();
    },
    [handleBackToListRequests, editDataForm]
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    validationSchema: SalaryCertificateFormSchema,
    onSubmit: handleSubmit,
  });

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(SalaryCertificateFormSchema, formik);

    if (!isFormValid) return;

    const body = {
      empNumber: editDataForm?.empNumber as string,
      requestId: editDataForm?.requestId,
      leaveTypeID: Number(formik.values.purpose),
      startDate: formik.values.requiredByDate,
      destination: formik.values.language,
      remarks: formik.values.remarks,
    };

    setDisableSaveForm(true);

    try {
      const { requestId, createdDate, reqStatusId } =
        await postSalaryForm(body).unwrap();

      dispatch(
        salaryCertificateActions.setSalaryCertificate({
          createdDate,
          requestId,
          reqStatusId,
        })
      );

      editSuccessNotification(String(requestId));

      setDisableSaveForm(false);
    } catch (error) {
      const message = (error as RequestError)?.data;
      errorNotification(message);

      setDisableSaveForm(false);
    }
  }, [editDataForm, formik, SalaryCertificateFormSchema]);

  useEffect(() => {
    if (editDataForm) {
      formik.setValues({
        purpose: String(editDataForm.leaveTypeID),
        requiredByDate: editDataForm.startDate,
        language: editDataForm.destination,
        remarks: editDataForm.remarks,
      });
    }
  }, [editDataForm]);

  const salaryTypeOptions = useMemo(() => {
    return transformDictToOptions(salaryTypes);
  }, [salaryTypes]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.reqStatusId === SalaryStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        disabledSaveForm={disabledSaveForm}
        formType={formType}
        showExtraUserFields
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            salaryTypeOptions={salaryTypeOptions}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${idRequest}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default SalaryCertificateEditForm;
